import { AppError } from '@app/errors/AppError'

import { toDeviceTime } from '@app/utils/clockOffset'
import { getJwtExp } from '@app/utils/jwt'

import { getAbortSignal } from '@app/packages/abortContext/actions'
import type { ChatServerToken } from '@app/packages/chat/SocketClient'

import { ensureGuestChatSession, hasGuestChatSessionSelector } from '@app/store/slices/guest_chat_session'
import { isAuthenticatedSelector } from '@app/store/slices/session'
import { createThunk } from '@app/store/thunk'

import { getAuthSession } from './contexts/authSession'

/**
 * The user-session JWT already carries `aud ∋ kidsout.chat`, so it is reused verbatim.
 * Guest fallback: `POST /api/v3/support/guest_session` is idempotent, so it doubles as
 * the refresh — for a guest who has one; raising it is the support widget's to do.
 */
export const getChatToken = () =>
  createThunk(async (dispatch, getState): Promise<ChatServerToken> => {
    const token = await dispatch(getAuthSession()).ensureAccessToken({ signal: dispatch(getAbortSignal()) })
    if (token !== null) {
      // The socket times its refresh by `Date.now()`, so a fast device would see the server's `exp` as already gone.
      const exp = getJwtExp(token)
      return { access_token: token, expires_at: exp === null ? null : toDeviceTime(exp) }
    }

    // A signed-in reader whose token could not be produced is not a guest. Raising one here
    // would swap their identity for an anonymous one with nothing said, which is how a support
    // thread arrives from somebody who plainly has an account (#127).
    if (isAuthenticatedSelector(getState())) throw new AppError('getChatToken: signed in, but no access token could be obtained')

    // Somebody signed out is not a guest until they open support. Raising one for whatever asked
    // for a token here is how a reader dropped by an expired session got a visitor of their own (front#130).
    if (!hasGuestChatSessionSelector(getState())) throw new AppError('getChatToken: no session and no guest chat')

    const guest = await dispatch(ensureGuestChatSession())

    return { access_token: guest.accessToken, expires_at: guest.expiresAt }
  })
